import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, deleteFn } from '@/lib/api';
import {
  Criterion,
  CreateCriterionDTO,
  UpdateCriterionDTO,
  CriterionType,
} from '@promo/shared';
import { useToast } from '@/hooks/use-toast';

/**
 * Custom hook for managing criteria data operations
 */
export function useCriteria(packetId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const criteriaCacheKey = ['criteria', packetId];

  // Query to fetch criteria list for a packet
  const criteriaQuery = useQuery<Criterion[], Error>({
    queryKey: criteriaCacheKey,
    queryFn: () => {
      if (!packetId) {
        return Promise.resolve([]);
      }
      return get<Criterion[]>('/criteria', { packetId });
    },
    enabled: !!packetId,
  });

  // Mutation to create a new criterion
  const createCriterionMutation = useMutation<Criterion, Error, CreateCriterionDTO>({
    mutationFn: (data) => post<Criterion>('/criteria', data),
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to create criterion: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Adjusted create function to accept onSuccess callback
  const createCriterion = (data: CreateCriterionDTO, options?: { onSuccess?: () => void }) => {
    createCriterionMutation.mutate(data, {
      onSuccess: () => { // Removed unused 'result' parameter
        queryClient.invalidateQueries({ queryKey: criteriaCacheKey });
        toast({
          title: 'Success',
          description: 'Criterion created successfully',
        });
        options?.onSuccess?.(); // Call the provided callback
      },
      // onError can remain in the mutation definition or be handled here too
    });
  };

  // Mutation to update an existing criterion
  const updateCriterionMutation = useMutation<
    Criterion,
    Error,
    { id: string; data: UpdateCriterionDTO }
  >({
    mutationFn: ({ id, data }) => put<Criterion>(`/criteria/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: criteriaCacheKey });
      toast({
        title: 'Success',
        description: 'Criterion updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update criterion: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Mutation to delete a criterion
  const deleteCriterionMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteFn<void>(`/criteria/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: criteriaCacheKey });
      toast({
        title: 'Success',
        description: 'Criterion deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to delete criterion: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Helper function to count criteria by type
  const getCriteriaCountByType = (type: CriterionType): number => {
    if (!criteriaQuery.data) return 0;
    return criteriaQuery.data.filter((c) => c.criterionType === type).length;
  };

  return {
    // Queries
    criteria: criteriaQuery.data || [],
    isLoadingCriteria: criteriaQuery.isLoading,
    criteriaError: criteriaQuery.error,
    
    // Mutations
    createCriterion: createCriterion, // Use the adjusted function
    isCreatingCriterion: createCriterionMutation.isPending,
    
    updateCriterion: (id: string, data: UpdateCriterionDTO) => 
      updateCriterionMutation.mutate({ id, data }),
    isUpdatingCriterion: updateCriterionMutation.isPending,
    
    deleteCriterion: (id: string) => deleteCriterionMutation.mutate(id),
    isDeletingCriterion: deleteCriterionMutation.isPending,
    
    // Helpers
    getCriteriaCountByType,
    isMutating: createCriterionMutation.isPending || 
      updateCriterionMutation.isPending || 
      deleteCriterionMutation.isPending,
      
    // Status
    refetchCriteria: criteriaQuery.refetch,
  };
} 